import React, { useState, useEffect, useMemo } from 'react';
import { Button, Pagination } from '@/components/atoms/index';
import AcademyVocabularyTable from '@/components/organisms/Table/AcademyVocabularyTable';
import AcademyWordTable from '@/components/organisms/Table/AcademyWordTable';
import { academyWordbookApi } from '@/services/api';

interface WordbookData {
  id: string;
  wordbookName: string;
  wordCount: number;
  isPublic: boolean;
  registrant: string;
  createdAt: string;
  sequenceNo?: number;
}

interface WordSelectionContentProps {
  onOk: (selectedWords: WordbookData[]) => void;
  onCancel: () => void;
  availableWords?: WordbookData[];
  title?: string;
  initialSelectedWords?: WordbookData[];
}

interface WordPreviewData {
  id: string;
  wordCode: string;
  word: string;
  meaning: string;
  ttsType: string;
  registrant: string;
  createdAt: string;
  uploadStatus: 'completed' | 'failed';
}


const sortByCreatedAtDesc = (list: WordbookData[]) => {
  const toTime = (value: string) => {
    const time = new Date(value).getTime();
    return Number.isNaN(time) ? 0 : time;
  };
  return [...list].sort((a, b) => {
    const diff = toTime(b.createdAt) - toTime(a.createdAt);
    if (diff !== 0) return diff;
    const idDiff = Number(b.id) - Number(a.id);
    if (!Number.isNaN(idDiff) && idDiff !== 0) return idDiff;
    return 0;
  });
};

const applySequenceNumbers = (list: WordbookData[]) => {
  const total = list.length;
  return list.map((wordbook, index) => ({
    ...wordbook,
    sequenceNo: total - index,
  }));
};

const WordSelectionContent: React.FC<WordSelectionContentProps> = ({
  onOk,
  onCancel,
  availableWords = [],
  title = '단어장 선택',
  initialSelectedWords = [],
}) => {
  const [selectedWords, setSelectedWords] = useState<WordbookData[]>(initialSelectedWords);
  const [searchText, setSearchText] = useState('');
  const [filteredAvailableWords, setFilteredAvailableWords] = useState<WordbookData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedWords, setDisplayedWords] = useState<WordbookData[]>([]);
  const [previewWords, setPreviewWords] = useState<WordPreviewData[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const pageSize = 10;

  // 사용 가능한 단어 목록
  const wordsToUse = useMemo(() => {
    return sortByCreatedAtDesc(availableWords);
  }, [availableWords]);

  // 검색어 변경 시 필터링
  useEffect(() => {
    const trimmed = searchText.trim().toLowerCase();
    const filtered = !trimmed
      ? wordsToUse
      : wordsToUse.filter(word =>
          word.wordbookName.toLowerCase().includes(trimmed)
        );
    setFilteredAvailableWords(applySequenceNumbers(filtered));
    // 검색어 변경 시 첫 페이지로 이동
    setCurrentPage(1);
  }, [searchText, wordsToUse]);

  // 페이지에 맞는 데이터 표시
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paged = filteredAvailableWords.slice(startIndex, endIndex);
    setDisplayedWords(paged);
  }, [filteredAvailableWords, currentPage, pageSize]);

  // initialSelectedWords가 변경되면 selectedWords 업데이트
  useEffect(() => {
    setSelectedWords(initialSelectedWords);
  }, [initialSelectedWords]);

  useEffect(() => {
    let cancelled = false;
    const loadPreviewWords = async () => {
      if (!selectedWords.length) {
        setPreviewWords([]);
        return;
      }
      setPreviewLoading(true);
      try {
        const results = await Promise.all(selectedWords.map(async (wordbook) => {
          try {
            const res = await academyWordbookApi.detail(wordbook.id);
            const data = res?.data || res;
            const words = Array.isArray(data?.words) ? data.words : [];
            return words.map((word: any) => ({
              id: String(word.id ?? word.wordId ?? `${wordbook.id}-${word.word ?? Math.random()}`),
              wordCode: word.code || '',
              word: word.word || '',
              meaning: word.meaning || '',
              ttsType: word.ttsType || `${word.gender || ''}/${word.age || ''}/${word.speed || ''}/${word.pitch || ''}`,
              registrant: word.registrant || '',
              createdAt: (word.createdAt || '').toString().substring(0, 10),
              uploadStatus: 'completed' as const,
            })) as WordPreviewData[];
          } catch {
            return [] as WordPreviewData[];
          }
        }));
        if (cancelled) return;
        const merged = ([] as WordPreviewData[]).concat(...results);
        const dedup = new Map<string, WordPreviewData>();
        merged.forEach((word) => {
          if (!dedup.has(word.id)) dedup.set(word.id, word);
        });
        setPreviewWords(Array.from(dedup.values()));
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };
    loadPreviewWords();
    return () => { cancelled = true; };
  }, [selectedWords]);

  // 페이지 변경 핸들러
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // 단어 선택/해제
  const handleWordSelection = (wordId: string, checked: boolean) => {
    if (checked) {
      const word = wordsToUse.find(w => w.id === wordId);
      if (word && !selectedWords.find(w => w.id === wordId)) {
        setSelectedWords(prev => [...prev, word]);
      }
    } else {
      setSelectedWords(prev => prev.filter(w => w.id !== wordId));
    }
  };

  // 행 클릭 시 선택/해제 토글
  const handleRowClick = (wordId: string) => {
    const isSelected = selectedWords.some(w => w.id === wordId);
    handleWordSelection(wordId, !isSelected);
  };

  // 확인 버튼 클릭
  const handleConfirm = () => {
    // 트랜지션을 위해 지연 후 콜백 호출
    setTimeout(() => {
      onOk(selectedWords);
    }, 200);
  };

  // 취소 버튼 클릭
  const handleCancel = () => {
    // 트랜지션을 위해 지연 후 콜백 호출
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
      </div>

      <div className="mb-4">
        <AcademyVocabularyTable
          wordbookData={displayedWords as any[]}
          type="word_exam_select"
          selectedIds={selectedWords.map(w => w.id)}
          onSelectionChange={handleWordSelection}
          onRowClick={handleRowClick}
          pagination={false}
          searchText={searchText}
          onSearchChange={setSearchText}
        />
      </div>
      {filteredAvailableWords.length > 0 && (
        <Pagination
          total={filteredAvailableWords.length}
          pageSize={pageSize}
          current={currentPage}
          onChange={handlePageChange}
        />
      )}
      {selectedWords.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold">포함된 단어 미리보기 ({previewWords.length}개)</h3>
            {previewLoading && <span className="text-xs text-gray-500">불러오는 중...</span>}
          </div>
          {previewLoading ? (
            <div className="py-4 text-center text-sm text-gray-500 bg-gray-50 rounded-md">단어를 불러오는 중입니다...</div>
          ) : previewWords.length > 0 ? (
            <div className="border border-gray-100 rounded-md max-h-[220px] overflow-y-auto text-sm">
              <AcademyWordTable
                data={previewWords}
                type="wordBookSelect"
                showCheckbox={false}
              />
            </div>
          ) : (
            <div className="py-4 text-center text-sm text-gray-500 bg-gray-50 rounded-md">
              선택된 단어장에 등록된 단어가 없습니다.
            </div>
          )}
        </div>
      )}
      <div className="flex gap-2 justify-end pt-4">
        <Button
          intent="secondary"
          onClick={handleCancel}
          className="min-w-[100px]"
        >
          취소
        </Button>
        <Button
          onClick={handleConfirm}
          className="min-w-[100px]"
        >
          선택 완료
        </Button>
      </div>
    </div>
  );
};

export default WordSelectionContent; 